
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  AppDialog,
  AppDialogScroll,
  AppDialogHeader,
  AppDialogBody,
  AppDialogFooter,
} from "@/components/ui/app-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Goal } from "@/types/todo";
import { useSubscription } from "@/hooks/useSubscription";
import { SubscriptionUpgradeDialog } from "@/components/subscription/SubscriptionUpgradeDialog";
import { Loader2, PenLine, CalendarDays, Tag } from "lucide-react";
import { useFormPersistence } from "@/hooks/useFormPersistence";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { BUCKET_LIST_CATEGORY } from "@/constants/categories";
import { MicButton } from "@/components/common/MicButton";

interface NewGoalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGoalCreated: (goal: Goal) => void;
  categories: string[];
  initialCategory?: string;
  initialTitle?: string;
}

interface GoalFormData {
  title: string;
  dueDate: string;
  category: string;
}

export function NewGoalDialog({ 
  open, 
  onOpenChange, 
  onGoalCreated, 
  categories,
  initialCategory = "",
  initialTitle = ""
}: NewGoalDialogProps) {
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [limitCheckResult, setLimitCheckResult] = useState<any>(null);
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  
  const [noDeadline, setNoDeadline] = useState(false);
  const { toast } = useToast();
  
  const { checkGoalLimit } = useSubscription();

  const formMethods = useForm<GoalFormData>({
    defaultValues: {
      title: initialTitle,
      dueDate: new Date().toISOString().split('T')[0],
      category: initialCategory || "Personal" // Default to "Personal" if no category specified
    }
  });

  const { clearPersistedForm } = useFormPersistence(
    formMethods,
    'new-goal-form-draft',
    open,
    onOpenChange
  );

  const currentCategory = formMethods.watch('category');
  const isBucketList = currentCategory === BUCKET_LIST_CATEGORY;

  // Auto-toggle noDeadline when Bucket List is selected
  useEffect(() => {
    if (isBucketList) {
      setNoDeadline(true);
    }
  }, [isBucketList]);

  // Set the initial values when the dialog opens
  useEffect(() => {
    if (open) {
      // Default to Goal mode every time the dialog opens — override any
      // persisted Bucket List draft so the "🎯 Goal" tab is pre-selected.
      // Respect an explicit initialCategory (intent picker passes BUCKET_LIST_CATEGORY for Dream).
      const targetCategory = initialCategory || 'Personal';
      formMethods.setValue('category', targetCategory);
      setNoDeadline(targetCategory === BUCKET_LIST_CATEGORY);

      // Always refresh the due date to today on open if the persisted/default value is empty or in the past
      const today = new Date().toISOString().split('T')[0];
      const currentDue = formMethods.getValues('dueDate');
      if (!currentDue || currentDue < today) {
        formMethods.setValue('dueDate', today);
      }

      if (initialTitle) {
        formMethods.setValue('title', initialTitle);
      }
    } else {
      setNoDeadline(false);
    }
  }, [open, initialCategory, initialTitle, formMethods]);


  const handleSubmit = async (data: GoalFormData) => {
    if (!data.title.trim()) {
      toast({
        title: "Error",
        description: "Goal title cannot be empty",
        variant: "destructive",
      });
      return;
    }
    
    // Check goal limit before creating
    setIsCheckingLimit(true);
    const limitResult = await checkGoalLimit();
    setIsCheckingLimit(false);
    
    if (!limitResult.can_create) {
      setLimitCheckResult(limitResult);
      setShowUpgradeDialog(true);
      return;
    }
    
    // Create the goal if limit check passes
    onGoalCreated({ 
      id: crypto.randomUUID(),
      title: data.title, 
      progress: 0, 
      dueDate: noDeadline ? "" : data.dueDate, 
      category: data.category, 
      todos: [],
      isExample: false 
    });
    
    formMethods.reset();
    clearPersistedForm();
    onOpenChange(false);
  };

  const handleModeChange = (mode: 'goal' | 'dream') => {
    if (mode === 'dream') {
      formMethods.setValue('category', BUCKET_LIST_CATEGORY);
      setNoDeadline(true);
    } else {
      if (currentCategory === BUCKET_LIST_CATEGORY) {
        formMethods.setValue('category', 'Personal');
      }
      setNoDeadline(false);
    }
  };

  return (
    <>
      <AppDialog
        open={open}
        onClose={() => onOpenChange(false)}
        size="md"
        ariaTitle={isBucketList ? "Add to Bucket List" : "Create New Goal"}
      >
        <AppDialogHeader
          title={
            isBucketList
              ? "Add to your Bucket List ✨"
              : initialCategory
                ? `Create New ${initialCategory} Goal`
                : "Create New Goal"
          }
        />
        <AppDialogScroll>
          <AppDialogBody>
            <form
              id="new-goal-form"
              onSubmit={formMethods.handleSubmit(handleSubmit)}
              className="space-y-4"
            >
              {/* Goal vs Dream mode toggle — sets the tone for everything below */}
              <div
                className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted"
                role="tablist"
                aria-label="Choose creation mode"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isBucketList}
                  onClick={() => handleModeChange('goal')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    !isBucketList
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  🎯 Goal <span className="hidden sm:inline text-xs opacity-70">(with deadline)</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isBucketList}
                  onClick={() => handleModeChange('dream')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    isBucketList
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  ✨ Dream <span className="hidden sm:inline text-xs opacity-70">(lifetime)</span>
                </button>
              </div>

              {/* Field 1 — what */}
              <div className="space-y-2 rounded-xl border border-border/60 bg-card p-3">
                <Label htmlFor="goal-title" className="flex items-center gap-2 text-sm font-semibold">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary"><PenLine className="h-3.5 w-3.5" aria-hidden /></span>
                  {isBucketList ? "What's your dream?" : "What do you want to achieve?"}
                </Label>
                <p className="text-xs text-muted-foreground">Keep it short and specific — you can add steps after.</p>
              <div className="flex gap-2">
                <Input
                  id="goal-title"
                  placeholder={isBucketList ? "e.g. See the Northern Lights, Visit Japan, Learn to scuba dive..." : "e.g. Run 5 km, Read 12 books, Visit all 7 Emirates"}
                  {...formMethods.register('title', { required: true })}
                  className="flex-1"
                />
                <MicButton
                  ariaLabel="Dictate goal title"
                  onTranscript={(spoken) => {
                    const current = (formMethods.getValues('title') || '').trim();
                    formMethods.setValue('title', current ? `${current} ${spoken}` : spoken, { shouldValidate: true });
                  }}
                />
              </div>
              {isBucketList && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <span>✨</span> Something you want to do — this weekend or someday. No deadline; we'll suggest UAE spots and partners that match.
                </p>
              )}
              </div>

              {/* Field 2 — when */}
              <div className="space-y-2 rounded-xl border border-border/60 bg-card p-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary"><CalendarDays className="h-3.5 w-3.5" aria-hidden /></span>
                    When?
                  </span>
                  <Label htmlFor="no-deadline" className="text-xs text-muted-foreground">
                    {isBucketList ? "Lifetime goal — no deadline" : "No deadline"}
                  </Label>
                  <Switch
                    id="no-deadline"
                    checked={noDeadline}
                    onCheckedChange={setNoDeadline}
                  />
                </div>
                {!noDeadline && (
                  <Input
                    type="date"
                    {...formMethods.register('dueDate')}
                    min={new Date().toISOString().split('T')[0]}
                  />
                )}
              </div>

              {/* Field 3 — category */}
              <div className="space-y-2 rounded-xl border border-border/60 bg-card p-3">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary"><Tag className="h-3.5 w-3.5" aria-hidden /></span>
                  Category
                </span>
                <p className="text-xs text-muted-foreground">Helps us suggest matching places and offers.</p>
              <Select
                value={formMethods.watch('category')}
                onValueChange={(value) => formMethods.setValue('category', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat === BUCKET_LIST_CATEGORY ? `🪣 ${cat}` : cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            </form>
          </AppDialogBody>
        </AppDialogScroll>
        <AppDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-goal-form"
            disabled={isCheckingLimit}
          >
            {isCheckingLimit ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : isBucketList ? (
              "Add to My Dreams ✨"
            ) : (
              "Create Goal"
            )}
          </Button>
        </AppDialogFooter>
      </AppDialog>


      <SubscriptionUpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        limitType="goals"
        currentCount={limitCheckResult?.current_count}
        limit={limitCheckResult?.limit}
      />
    </>
  );
}
